/**
 * ProjectDetails HealthStatusBadge wrapper
 * Bridges the gap between ProjectDetails' capitalized status format
 * and the common HealthIndicator component (lowercase format)
 */

import React from 'react';

import { type HealthColor, HealthDot } from '@/components/common/HealthIndicator';
import { Pill, type PillTone } from '@/components/common/Pill';
import { normalizeHealthStatus } from '@/utils/statusConverters';

export type HealthStatusType = 'Red' | 'Yellow' | 'Green' | 'pending';

export interface HealthStatusBadgeProps {
  status?: HealthStatusType;
  health?: HealthStatusType;
  message?: string;
  size?: 'small' | 'default';
  variant?: 'badge' | 'dot';
}

/**
 * HealthStatusBadge — Wrapper around common HealthIndicator
 * Provides backwards compatibility for ProjectDetails components
 * while using the centralized HealthIndicator from the design system
 */
export function HealthStatusBadge({
  status,
  health,
  message,
  size: _size = 'default',
  variant = 'badge',
}: Readonly<HealthStatusBadgeProps>): React.ReactElement {
  // Support both 'status' (new) and 'health' (old) props for backwards compatibility
  const statusValue = status || health;
  if (!statusValue) {
    return <span />;
  }

  // Convert from capitalized (Red) to lowercase (red)
  const normalizedStatus = normalizeHealthStatus(statusValue);

  // Render just the glyph for 'dot' variant
  if (variant === 'dot') {
    if (normalizedStatus === 'pending') {
      return <span />;
    }
    return <HealthDot status={normalizedStatus as HealthColor} />;
  }

  // Full badge variant uses the shared pill geometry and semantic tone.
  const statusLabel = statusValue.charAt(0).toUpperCase() + statusValue.slice(1).toLowerCase();
  const tone: PillTone =
    normalizedStatus === 'green'
      ? 'success'
      : normalizedStatus === 'yellow'
        ? 'warning'
        : normalizedStatus === 'red'
          ? 'error'
          : 'info';

  return (
    <Pill
      tone={tone}
      size={_size}
      leading={
        normalizedStatus !== 'pending' ? (
          <HealthDot status={normalizedStatus as HealthColor} />
        ) : undefined
      }
    >
      {message || statusLabel}
    </Pill>
  );
}

export default HealthStatusBadge;
